import { useState } from 'react'
import { Link } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchFleetOverview, unsubscribeFleetSeverity } from '@/features/fleetAlarms/api'
import type { FleetSeverity } from '@/features/fleetAlarms/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import ConfirmModal from '@/components/common/ConfirmModal'

/** Ports includes/fleet_alarm_subscriptions.html — per-severity fleet
 * default overview, scoped server-side to the current user's accessible
 * controllers. Each severity lists its subscribed systems so they can be
 * removed one by one or all at once. */
export default function FleetAlarmSubscriptionsPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['fleet-alarm-overview'],
    queryFn: fetchFleetOverview,
  })

  if (isLoading) return <LoadingState label="Loading fleet subscriptions…" />
  if (isError) {
    return (
      <ErrorState message={error instanceof Error ? error.message : 'Failed to load.'} onRetry={() => refetch()} />
    )
  }
  if (!data) return null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink">Subscriptions — All Systems</h1>
        <Link to="/fleet-alarm-subscriptions/manage" className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90">
          Manage Subscriptions
        </Link>
      </div>

      {data.new_systems.length > 0 && (
        <div className="rounded-2xl border border-brand bg-card p-3 text-sm text-text-primary">
          {data.new_systems.length} new system(s) recently added:{' '}
          {data.new_systems.map((s) => s.controller_name || s.controller_id).join(', ')}
        </div>
      )}

      {data.severities.map((sev) => (
        <SeverityCard key={sev.alarm_severity_id} severity={sev} />
      ))}
    </div>
  )
}

function SeverityCard({ severity: sev }: { severity: FleetSeverity }) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [confirmAll, setConfirmAll] = useState(false)
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null)

  const unsubscribeMutation = useMutation({
    mutationFn: (target: { controllerIds: string[] } | { all: true }) =>
      unsubscribeFleetSeverity(sev.alarm_severity_id, target),
    onSuccess: (result, target) => {
      setConfirmAll(false)
      setMessage({
        text:
          'all' in target
            ? `Unsubscribed from ${sev.alarm_severity_name} on all systems; the fleet default is now off.`
            : `Removed ${result.systems_unsubscribed} system(s) from ${sev.alarm_severity_name}.`,
        isError: false,
      })
      queryClient.invalidateQueries({ queryKey: ['fleet-alarm-overview'] })
    },
    onError: (err) => {
      setConfirmAll(false)
      const apiError = isAxiosError(err) ? (err.response?.data as { error?: string } | undefined)?.error : undefined
      setMessage({ text: apiError || 'Failed to unsubscribe. Please try again.', isError: true })
    },
  })
  const pendingId =
    unsubscribeMutation.isPending && unsubscribeMutation.variables && 'controllerIds' in unsubscribeMutation.variables
      ? unsubscribeMutation.variables.controllerIds[0]
      : null

  const systems = [...sev.subscribed_systems].sort((a, b) =>
    (a.controller_name || a.controller_id).localeCompare(b.controller_name || b.controller_id),
  )

  return (
    <div className="rounded-2xl border border-nav-line bg-card p-5" data-testid={`severity-${sev.alarm_severity_id}`}>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">{sev.alarm_severity_name}</h2>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            sev.is_default_subscribed ? 'bg-brand text-white' : 'bg-bg-tertiary text-text-secondary'
          }`}
        >
          Default {sev.is_default_subscribed ? 'ON' : 'OFF'}
        </span>
      </div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-text-secondary">
          {sev.systems_affected} of {sev.total_systems} system(s) subscribed
        </p>
        {systems.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="whitespace-nowrap rounded-full border border-nav-line px-3 py-1 text-xs font-semibold text-text-primary hover:bg-nav-hover"
            >
              {expanded ? 'Hide systems' : 'Show systems'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmAll(true)}
              disabled={unsubscribeMutation.isPending}
              className="whitespace-nowrap rounded-full border border-error px-3 py-1 text-xs font-semibold text-error hover:bg-nav-hover disabled:opacity-50"
            >
              Unsubscribe all
            </button>
          </div>
        )}
      </div>

      {message && (
        <p role="status" className={`mb-2 text-xs ${message.isError ? 'text-error' : 'text-brand'}`}>
          {message.text}
        </p>
      )}

      {expanded && systems.length > 0 && (
        <ul className="mb-2 max-h-72 divide-y divide-nav-line overflow-y-auto rounded-2xl border border-nav-line">
          {systems.map((s) => (
            <li key={s.controller_id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
              <span className="truncate text-text-primary">{s.controller_name || s.controller_id}</span>
              <button
                type="button"
                onClick={() => unsubscribeMutation.mutate({ controllerIds: [s.controller_id] })}
                disabled={unsubscribeMutation.isPending}
                aria-label={`Remove ${s.controller_name || s.controller_id} from ${sev.alarm_severity_name}`}
                className="shrink-0 rounded-full border border-nav-line px-3 py-0.5 text-xs font-semibold text-error hover:bg-nav-hover disabled:opacity-50"
              >
                {pendingId === s.controller_id ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {sev.override_systems.length > 0 && (
        <p className="text-xs text-text-secondary">
          {sev.override_systems.length} system(s) deviate from this fleet default:{' '}
          {sev.override_systems.map((s) => s.controller_name || s.controller_id).join(', ')}
        </p>
      )}

      <ConfirmModal
        isOpen={confirmAll}
        title={`Unsubscribe from ${sev.alarm_severity_name}?`}
        message={`You will stop receiving ${sev.alarm_severity_name} alarms from all ${sev.systems_affected} subscribed system(s), and the fleet default for ${sev.alarm_severity_name} will be turned off, so systems added later won't subscribe you either.`}
        confirmLabel="Unsubscribe all"
        isConfirming={unsubscribeMutation.isPending}
        onConfirm={() => unsubscribeMutation.mutate({ all: true })}
        onCancel={() => setConfirmAll(false)}
      />
    </div>
  )
}
